import React, { useEffect, useMemo, useState } from 'react';
import { consultationService } from '../services/api';
import CIE10Selector from './CIE10Selector';
import { CONSULTA_FIELD_LABELS, formatAuditValue } from './fieldLabels';
import './EditRecordModal.css';

const toDatetimeInputValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const TEXT_FIELDS = [
  'aseguradora', 'medio_llegada', 'fracturas', 'enfermedades_previas',
  'hospitalizaciones_previas', 'fumas_o_tomas', 'adicciones',
  'antecedentes_familiares', 'antecedentes_personales_no_patologicos',
  'antecedentes_personales_patologicos', 'antecedentes_gineco_obstetricos',
  'motivo_consulta', 'interrogatorio', 'exploracion_fisica', 'tratamiento',
  'solicitud_laboratorio', 'resultados_laboratorio', 'pronostico',
];

const NUMBER_FIELDS = [
  'peso', 'talla', 'temperatura', 'tension_arterial_sistolica',
  'tension_arterial_diastolica', 'frecuencia_cardiaca', 'frecuencia_respiratoria',
];

const buildFormFromConsulta = (consulta) => {
  const form = {};
  [...TEXT_FIELDS, ...NUMBER_FIELDS].forEach((field) => {
    form[field] = consulta[field] === null || consulta[field] === undefined
      ? ''
      : String(consulta[field]);
  });
  form.fecha_ingreso = toDatetimeInputValue(consulta.fecha_ingreso);
  form.solicitar_laboratorio = Boolean(consulta.solicitar_laboratorio);
  return form;
};

const diagnosticsToCodes = (diagnostics) => (
  (diagnostics || []).map((d) => d.codigo).filter(Boolean).join(',')
);

const EditConsultationModal = ({ consultaId, onClose, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [consulta, setConsulta] = useState(null);
  const [form, setForm] = useState(null);
  const [initialForm, setInitialForm] = useState(null);
  const [diagnostics, setDiagnostics] = useState([]);
  const [initialDiagnostics, setInitialDiagnostics] = useState([]);
  const [step, setStep] = useState('form');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const result = await consultationService.getById(consultaId);
        if (cancelled) return;
        const data = result.data;
        const builtForm = buildFormFromConsulta(data);
        const diags = Array.isArray(data.diagnostico_cie10)
          ? data.diagnostico_cie10.filter(Boolean)
          : [];
        setConsulta(data);
        setForm(builtForm);
        setInitialForm(builtForm);
        setDiagnostics(diags);
        setInitialDiagnostics(diags);
      } catch (err) {
        if (!cancelled) setError('No se pudo cargar la consulta para editar');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [consultaId]);

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const changedFields = useMemo(() => {
    if (!form || !initialForm) return [];
    const changed = Object.keys(form).filter((field) => form[field] !== initialForm[field]);
    if (diagnosticsToCodes(diagnostics) !== diagnosticsToCodes(initialDiagnostics)) {
      changed.push('diagnostico_cie10');
    }
    return changed;
  }, [form, initialForm, diagnostics, initialDiagnostics]);

  const handleReview = () => {
    setError('');
    if (!String(form.motivo_consulta || '').trim()) {
      setError('El motivo de consulta no puede quedar vacío');
      return;
    }
    if (form.solicitar_laboratorio && !String(form.solicitud_laboratorio || '').trim()) {
      setError('Debe indicar el detalle de la solicitud de laboratorio');
      return;
    }
    setStep('confirm');
  };

  const handleConfirm = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {};
      changedFields.forEach((field) => {
        if (field === 'diagnostico_cie10') {
          payload.diagnostico_cie10 = diagnostics.map((d) => d.codigo);
        } else if (field === 'solicitar_laboratorio') {
          payload.solicitar_laboratorio = form.solicitar_laboratorio;
        } else {
          payload[field] = form[field] === '' ? null : form[field];
        }
      });
      // La validación del backend usa ambos campos de laboratorio en conjunto
      if ('solicitar_laboratorio' in payload && !('solicitud_laboratorio' in payload)) {
        payload.solicitud_laboratorio = form.solicitud_laboratorio === '' ? null : form.solicitud_laboratorio;
      }

      const result = await consultationService.update(consultaId, payload);
      if (result.success) {
        onSaved();
      } else {
        setError(result.message || 'No se pudo guardar la consulta');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error al guardar los cambios');
    } finally {
      setSaving(false);
    }
  };

  const renderChangeRow = (field) => {
    let before;
    let after;
    if (field === 'diagnostico_cie10') {
      before = initialDiagnostics.map((d) => d.codigo);
      after = diagnostics.map((d) => d.codigo);
    } else {
      before = initialForm[field];
      after = form[field];
    }
    return (
      <tr key={field}>
        <td>{CONSULTA_FIELD_LABELS[field] || field}</td>
        <td><span className="confirm-value-before">{formatAuditValue(before)}</span></td>
        <td><span className="confirm-value-after">{formatAuditValue(after)}</span></td>
      </tr>
    );
  };

  const numberInput = (field, stepValue = '0.01') => (
    <div className="edit-form-field" key={field}>
      <label>{CONSULTA_FIELD_LABELS[field]}</label>
      <input
        type="number"
        step={stepValue}
        value={form[field]}
        onChange={(e) => setField(field, e.target.value)}
      />
    </div>
  );

  const textArea = (field, fullWidth = true) => (
    <div className={`edit-form-field ${fullWidth ? 'full-width' : ''}`} key={field}>
      <label>{CONSULTA_FIELD_LABELS[field]}</label>
      <textarea
        value={form[field]}
        onChange={(e) => setField(field, e.target.value)}
      />
    </div>
  );

  return (
    <div className="edit-modal-backdrop" role="presentation">
      <div className="edit-modal" role="dialog" aria-modal="true" aria-labelledby="edit-consulta-title">
        <div className="edit-modal-header">
          <h3 id="edit-consulta-title">
            {step === 'form'
              ? `Editar Consulta #${consultaId}`
              : 'Confirmar cambios'}
          </h3>
          <button type="button" className="edit-modal-close" onClick={onClose} aria-label="Cerrar">×</button>
        </div>

        <div className="edit-modal-body">
          {error && <div className="edit-modal-error">{error}</div>}

          {loading && <p>Cargando consulta...</p>}

          {!loading && form && step === 'form' && (
            <>
              <div className="edit-modal-section">
                <h4>Signos Vitales</h4>
                <div className="edit-form-grid">
                  {numberInput('peso')}
                  {numberInput('talla')}
                  {numberInput('temperatura', '0.1')}
                  {numberInput('tension_arterial_sistolica', '1')}
                  {numberInput('tension_arterial_diastolica', '1')}
                  {numberInput('frecuencia_cardiaca', '1')}
                  {numberInput('frecuencia_respiratoria', '1')}
                </div>
              </div>

              <div className="edit-modal-section">
                <h4>Información de Ingreso</h4>
                <div className="edit-form-grid">
                  <div className="edit-form-field">
                    <label>{CONSULTA_FIELD_LABELS.aseguradora}</label>
                    <input type="text" value={form.aseguradora} onChange={(e) => setField('aseguradora', e.target.value)} />
                  </div>
                  <div className="edit-form-field">
                    <label>{CONSULTA_FIELD_LABELS.medio_llegada}</label>
                    <input type="text" value={form.medio_llegada} onChange={(e) => setField('medio_llegada', e.target.value)} />
                  </div>
                  <div className="edit-form-field">
                    <label>{CONSULTA_FIELD_LABELS.fecha_ingreso}</label>
                    <input type="datetime-local" value={form.fecha_ingreso} onChange={(e) => setField('fecha_ingreso', e.target.value)} />
                  </div>
                </div>
              </div>

              <div className="edit-modal-section">
                <h4>Historial Médico</h4>
                <div className="edit-form-grid">
                  {textArea('fracturas')}
                  {textArea('enfermedades_previas')}
                  {textArea('hospitalizaciones_previas')}
                  {textArea('fumas_o_tomas')}
                  {textArea('adicciones')}
                </div>
              </div>

              <div className="edit-modal-section">
                <h4>Antecedentes</h4>
                <div className="edit-form-grid">
                  {textArea('antecedentes_familiares')}
                  {textArea('antecedentes_personales_patologicos')}
                  {textArea('antecedentes_personales_no_patologicos')}
                  {(consulta?.genero === 'Femenino' || form.antecedentes_gineco_obstetricos) &&
                    textArea('antecedentes_gineco_obstetricos')}
                </div>
              </div>

              <div className="edit-modal-section">
                <h4>Consulta</h4>
                <div className="edit-form-grid">
                  {textArea('motivo_consulta')}
                  {textArea('interrogatorio')}
                  {textArea('exploracion_fisica')}
                </div>
              </div>

              <div className="edit-modal-section">
                <h4>Diagnóstico CIE-10</h4>
                <CIE10Selector selectedDiagnostics={diagnostics} onChange={setDiagnostics} />
              </div>

              <div className="edit-modal-section">
                <h4>Tratamiento y Seguimiento</h4>
                <div className="edit-form-grid">
                  {textArea('tratamiento')}
                  <div className="edit-form-field full-width">
                    <label className="edit-form-checkbox">
                      <input
                        type="checkbox"
                        checked={form.solicitar_laboratorio}
                        onChange={(e) => setField('solicitar_laboratorio', e.target.checked)}
                      />
                      {CONSULTA_FIELD_LABELS.solicitar_laboratorio}
                    </label>
                  </div>
                  {form.solicitar_laboratorio && textArea('solicitud_laboratorio')}
                  {textArea('resultados_laboratorio')}
                  {textArea('pronostico')}
                </div>
              </div>
            </>
          )}

          {!loading && form && step === 'confirm' && (
            changedFields.length === 0 ? (
              <p className="confirm-no-changes">No hay cambios por guardar.</p>
            ) : (
              <>
                <p className="confirm-intro">
                  Revisa los cambios antes de confirmar. Cada edición queda registrada con
                  fecha, hora y doctor para trazabilidad del expediente.
                </p>
                <table className="confirm-changes-table">
                  <thead>
                    <tr>
                      <th>Campo</th>
                      <th>Antes</th>
                      <th>Después</th>
                    </tr>
                  </thead>
                  <tbody>
                    {changedFields.map(renderChangeRow)}
                  </tbody>
                </table>
              </>
            )
          )}
        </div>

        <div className="edit-modal-footer">
          {step === 'form' && (
            <>
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleReview}
                disabled={loading || !form || changedFields.length === 0}
              >
                Revisar cambios
              </button>
            </>
          )}
          {step === 'confirm' && (
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setStep('form')} disabled={saving}>
                Volver a editar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleConfirm}
                disabled={saving || changedFields.length === 0}
              >
                {saving ? 'Guardando...' : 'Confirmar'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditConsultationModal;
